import React from "react";
import {
  Users,
  Building,
  Crown,
  UserCog,
  MapPin,
  Package,
  Headphones,
  Phone,
  Globe,
  TrendingUp,
  Activity,
  AlertCircle,
} from "lucide-react";
import { User, Team, UserStats, getRoleIcon, getRoleColor } from "./types";

interface StatsTabProps {
  users: User[];
  teams: Team[];
  stats: UserStats | null;
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
}

export const StatsTab: React.FC<StatsTabProps> = ({
  users,
  teams,
  stats,
  loading,
  error,
  onRefresh,
}) => {
  const getUsersByRole = (role: string) => {
    return users.filter((user) => user.role === role);
  };

  const getUsersByCountry = (country: string) => {
    return users.filter((user) => user.countries.includes(country));
  };

  const getActiveUsers = () => {
    return users.filter((user) => user.isActive);
  };

  const getInactiveUsers = () => {
    return users.filter((user) => !user.isActive);
  };

  const getUsersWithRecentLogin = () => {
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

    return users.filter((user) => {
      if (!user.lastLogin) return false;
      return new Date(user.lastLogin) > oneWeekAgo;
    });
  };

  const availableCountries = [
    "Albania",
    "Bulgaria",
    "Greece",
    "Kosovo",
    "Serbia",
    "The Republic of Macedonia",
  ];

  const roleStats = [
    { role: "admin", label: "Admins", icon: Crown, color: "text-purple-600" },
    {
      role: "manager",
      label: "Managers",
      icon: UserCog,
      color: "text-blue-600",
    },
    {
      role: "country-manager",
      label: "Country Managers",
      icon: MapPin,
      color: "text-green-600",
    },
    {
      role: "country-post-manager",
      label: "Post Managers",
      icon: Package,
      color: "text-indigo-600",
    },
    {
      role: "call-center",
      label: "Call Center",
      icon: Headphones,
      color: "text-orange-600",
    },
    {
      role: "call-operator",
      label: "Call Operators",
      icon: Phone,
      color: "text-blue-600",
    },
    {
      role: "post-operator",
      label: "Post Operators",
      icon: Package,
      color: "text-purple-600",
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#264a3e]"></div>
        <span className="ml-2 text-gray-600">Loading statistics...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center">
          <AlertCircle className="w-5 h-5 text-red-400 mr-2" />
          <p className="text-red-800">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-medium text-gray-900">User Statistics</h2>
          <p className="text-sm text-gray-600">
            Overview of user activity and distribution
          </p>
        </div>
      </div>

      {/* Main Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <Users className="w-8 h-8 text-blue-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Total Users</p>
              <p className="text-2xl font-semibold text-gray-900">
                {users.length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <Activity className="w-8 h-8 text-green-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Active Users</p>
              <p className="text-2xl font-semibold text-gray-900">
                {getActiveUsers().length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <Building className="w-8 h-8 text-purple-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Total Teams</p>
              <p className="text-2xl font-semibold text-gray-900">
                {teams.length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <TrendingUp className="w-8 h-8 text-orange-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Recent Logins</p>
              <p className="text-2xl font-semibold text-gray-900">
                {getUsersWithRecentLogin().length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Role Distribution */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Users by Role
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {roleStats.map(({ role, label, icon: Icon, color }) => {
              const roleUsers = getUsersByRole(role);
              const percentage =
                users.length > 0 ? (roleUsers.length / users.length) * 100 : 0;

              return (
                <div
                  key={role}
                  className="border border-gray-200 rounded-lg p-4"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center">
                      <Icon className={`w-5 h-5 ${color} mr-2`} />
                      <h4 className="text-sm font-medium text-gray-900">
                        {label}
                      </h4>
                    </div>
                    <span className="text-sm font-semibold text-gray-900">
                      {roleUsers.length}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-[#264a3e] h-2 rounded-full"
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {percentage.toFixed(1)}% of total
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Activity Overview */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Activity Overview
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600">
                {getActiveUsers().length}
              </div>
              <div className="text-sm text-gray-500">Active Users</div>
              <div className="text-xs text-gray-400 mt-1">
                {users.length > 0
                  ? ((getActiveUsers().length / users.length) * 100).toFixed(1)
                  : 0}
                % of total
              </div>
            </div>

            <div className="text-center">
              <div className="text-3xl font-bold text-red-600">
                {getInactiveUsers().length}
              </div>
              <div className="text-sm text-gray-500">Inactive Users</div>
              <div className="text-xs text-gray-400 mt-1">
                {users.length > 0
                  ? ((getInactiveUsers().length / users.length) * 100).toFixed(
                      1
                    )
                  : 0}
                % of total
              </div>
            </div>

            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600">
                {getUsersWithRecentLogin().length}
              </div>
              <div className="text-sm text-gray-500">
                Recent Logins (7 days)
              </div>
              <div className="text-xs text-gray-400 mt-1">
                {users.length > 0
                  ? (
                      (getUsersWithRecentLogin().length / users.length) *
                      100
                    ).toFixed(1)
                  : 0}
                % of total
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Team Statistics */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Team Statistics
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-3">
                Team Types
              </h4>
              <div className="space-y-2">
                {["call-center", "post-office", "management", "support"].map(
                  (type) => {
                    const typeTeams = teams.filter(
                      (team) => team.type === type
                    );
                    return (
                      <div
                        key={type}
                        className="flex items-center justify-between"
                      >
                        <span className="text-sm text-gray-600 capitalize">
                          {type.replace("-", " ")}
                        </span>
                        <span className="text-sm font-medium text-gray-900">
                          {typeTeams.length}
                        </span>
                      </div>
                    );
                  }
                )}
              </div>
            </div>

            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-3">
                Average Team Size
              </h4>
              <div className="text-2xl font-bold text-[#264a3e]">
                {teams.length > 0
                  ? (users.length / teams.length).toFixed(1)
                  : 0}
              </div>
              <p className="text-sm text-gray-500">users per team</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatsTab;
